/* Bernard James — product page interactions for the editorial-split (BJ-DS
   "Shopify Final") layout. Loaded only by sections/main-product.liquid when
   layout_style is editorial-split, so no other template pays for it.

   Four small behaviours, kept in one file because they all hang off the same
   page and the section only loads one script:

   1. Size-dropdown placeholder. Shopify always pre-selects a variant, so
      `option.selected_value` is never empty and the size control would open
      claiming a size the shopper never chose. When the variant_picker block
      sets a placeholder we show that instead and hold the submit button
      disabled until they pick.

   2. Open/closed label. The design's open state always reads "SELECT SIZE",
      whatever is currently chosen, and flips the chevron. The list's `.active`
      class is owned by assets/global.js (jQuery), so we observe it rather than
      fight it for the click.

   3. Sold-out swap. The design REPLACES Add to bag with Contact advisor rather
      than disabling it, and drops "More payment methods" with it. The theme
      only ever toggles `disabled` on the submit (global.js toggleAddButton), so
      that attribute is the signal; the swap itself is CSS driven by
      `data-bj-soldout` on the buy-buttons wrapper.

   4. Description clamp. Lifts the three-line clamp behind "Read more", and
      hides the control entirely when the copy is short enough not to clip.

   Progressive: with no JS the label shows the real selected value, the button
   stays enabled, the size list still opens (it is a plain CSS `.active`
   toggle), and the description reads truncated — all stock behaviour. */
(function () {
  var PLACEHOLDER_ATTR = 'data-select-placeholder';
  var UNSELECTED_CLASS = 'select--placeholder';
  var OPEN_CLASS = 'is-open';

  /* ---------------------------------------------------------------- utils */

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function submitButtonsFor(fieldset) {
    var form = fieldset.closest('.product__info-container, .product-section, form');
    if (!form) form = document;
    return form.querySelectorAll('.product-form__submit');
  }

  /* --------------------------------------------------- 1 + 2. size select */

  function currentValue(fieldset) {
    var checked = fieldset.querySelector('.dropdown-select input:checked');
    if (!checked) return '';
    var row = checked.closest('li');
    var value = row && row.querySelector('.select-labels');
    return (value ? value.textContent : checked.value || '').trim();
  }

  function render(fieldset) {
    var label = fieldset.querySelector('.dropdown-select .select-label');
    if (!label) return;

    var placeholder = fieldset.getAttribute(PLACEHOLDER_ATTR);
    var selected = fieldset.dataset.bjSelected || '';
    var open = fieldset.classList.contains(OPEN_CLASS);

    // Open always asks for a choice; closed shows the choice, or the
    // placeholder while there isn't one yet.
    var usePlaceholder = !!placeholder && (open || !selected);

    // Keep the <span> the theme's own dropdown handler writes into.
    var span = document.createElement('span');
    // "SIZE 8" on the DS page, "Size: 8" everywhere else this snippet renders.
    var separator = fieldset.hasAttribute('data-bj-size-select') ? ' ' : ': ';
    span.textContent = usePlaceholder ? placeholder : selected;
    label.textContent = usePlaceholder ? '' : fieldset.dataset.optionLabel + separator;
    label.appendChild(span);

    fieldset.classList.toggle(UNSELECTED_CLASS, !selected);
  }

  function lockSubmit(fieldset, locked) {
    each(submitButtonsFor(fieldset), function (button) {
      if (locked) {
        // Remember whether the button was already disabled (sold out) so we
        // never re-enable something that should stay off — and so the sold-out
        // reading below can still see the real state through our lock.
        if (button.dataset.bjWasDisabled === undefined) {
          button.dataset.bjWasDisabled = button.disabled ? 'true' : 'false';
        }
        button.disabled = true;
      } else if (button.dataset.bjWasDisabled !== undefined) {
        button.disabled = button.dataset.bjWasDisabled === 'true';
        delete button.dataset.bjWasDisabled;
      }
    });
    syncBuyState(document);
  }

  function watchOpenState(fieldset) {
    var list = fieldset.querySelector('.dropdown-select ul');
    if (!list || typeof MutationObserver !== 'function') return;

    new MutationObserver(function () {
      var open = list.classList.contains('active');
      if (fieldset.classList.contains(OPEN_CLASS) === open) return;
      fieldset.classList.toggle(OPEN_CLASS, open);
      render(fieldset);
    }).observe(list, { attributes: true, attributeFilter: ['class'] });
  }

  function initSizeSelects(root) {
    each((root || document).querySelectorAll('[data-bj-size-select], [' + PLACEHOLDER_ATTR + ']'), function (fieldset) {
      if (fieldset.dataset.bjPlaceholderReady === 'true') return;
      fieldset.dataset.bjPlaceholderReady = 'true';

      var label = fieldset.querySelector('.dropdown-select .select-label');
      if (!label) return;

      // Stash the option name ("Size") before we overwrite the label. Read the
      // bare text nodes rather than splitting on ':' — the DS drops the colon,
      // so a naive split would swallow the value too and render "SIZE 8 8".
      var name = '';
      each(label.childNodes, function (node) {
        if (node.nodeType === 3) name += node.textContent;
      });
      fieldset.dataset.optionLabel = name.replace(':', '').replace(/\s+/g, ' ').trim();
      fieldset.dataset.bjSelected = '';

      watchOpenState(fieldset);

      if (!fieldset.hasAttribute(PLACEHOLDER_ATTR)) {
        // No placeholder configured: the server-rendered label is already
        // truthful, so only the open/closed chrome is ours to manage.
        fieldset.dataset.bjSelected = currentValue(fieldset);
        return;
      }

      render(fieldset);
      lockSubmit(fieldset, true);
    });
  }

  document.addEventListener('click', function (event) {
    var item = event.target.closest('.dropdown-select ul li');
    if (!item) return;

    var fieldset = item.closest('[data-bj-size-select], [' + PLACEHOLDER_ATTR + ']');
    if (!fieldset) return;

    // A row without a radio is not an option — outside the DS page the list
    // also carries the "Size Guide" opener — so it must not read as a choice.
    var input = item.querySelector('input');
    if (!input || input.classList.contains('disabled')) return;

    // Read the value from .select-labels rather than the whole row: rows also
    // render an inventory indicator, which textContent would fold into the
    // label as "8 IN STOCK".
    var value = item.querySelector('.select-labels');
    fieldset.dataset.bjSelected = (value || item).textContent.trim();

    var wasUnselected = fieldset.classList.contains(UNSELECTED_CLASS);
    render(fieldset);
    if (wasUnselected) lockSubmit(fieldset, false);
  });

  /* ------------------------------------------------ 3. sold-out state swap */

  function isSoldOut(button) {
    // Our own placeholder lock stores the real state, so read through it —
    // otherwise "no size chosen yet" would read as sold out and swap the
    // buttons on a perfectly available product.
    if (button.dataset.bjWasDisabled !== undefined) {
      return button.dataset.bjWasDisabled === 'true';
    }
    return button.disabled;
  }

  function applyBuyState(wrapper) {
    var button = wrapper.querySelector('.product-form__submit');
    if (!button) return;
    if (isSoldOut(button)) {
      wrapper.setAttribute('data-bj-soldout', 'true');
    } else {
      wrapper.removeAttribute('data-bj-soldout');
    }
  }

  function syncBuyState(root) {
    var scope = root || document;
    // querySelectorAll only walks descendants, so a scope that IS the wrapper
    // would match nothing — which is exactly how the per-wrapper observer
    // calls this.
    if (scope.matches && scope.matches('[data-bj-buy]')) {
      applyBuyState(scope);
      return;
    }
    each(scope.querySelectorAll('[data-bj-buy]'), applyBuyState);
  }

  function watchBuyState(root) {
    if (typeof MutationObserver !== 'function') return;
    each((root || document).querySelectorAll('[data-bj-buy]'), function (wrapper) {
      if (wrapper.dataset.bjBuyReady === 'true') return;
      var button = wrapper.querySelector('.product-form__submit');
      if (!button) return;
      wrapper.dataset.bjBuyReady = 'true';

      new MutationObserver(function () {
        syncBuyState(wrapper);
      }).observe(button, { attributes: true, attributeFilter: ['disabled', 'data-bj-was-disabled'] });
    });
    syncBuyState(root);
  }

  /* ------------------------------------ 3b. colour row overflow affordance */

  // `is-scrollable` = there is row to the right (fade + next arrow).
  // `is-scrolled`   = there is row to the left  (prev arrow).
  function syncSwatchRow(fieldset, row) {
    fieldset.classList.toggle(
      'is-scrollable',
      row.scrollWidth - row.clientWidth - row.scrollLeft > 1
    );
    fieldset.classList.toggle('is-scrolled', row.scrollLeft > 1);
  }

  function measureSwatchRow(root) {
    each((root || document).querySelectorAll('.product-form__controls--colors'), function (fieldset) {
      var row = fieldset.querySelector('.product-form__controls-group');
      if (!row) return;
      // The fade and its arrows only mean something when there is more row to
      // scroll to; on a two-colour product they would be inventing an
      // affordance for content that isn't there.
      syncSwatchRow(fieldset, row);
    });
  }

  // The arrows are injected rather than authored into
  // product-variant-picker.liquid: that snippet is shared by every product
  // layout, and a control only JS can operate should only exist when JS is
  // running. Without JS the row still swipes natively (overflow-x: auto), so
  // nothing is lost — the arrows are the desktop/pointer affordance, where a
  // horizontal scroll otherwise needs shift+wheel.
  function makeSwatchNav(fieldset, direction, label) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'bj-swatch-nav bj-swatch-nav--' + direction;
    button.setAttribute('aria-label', label);
    fieldset.appendChild(button);
    return button;
  }

  function watchSwatchRow(root) {
    each((root || document).querySelectorAll('.product-form__controls--colors'), function (fieldset) {
      // Presence of the controls, not a one-shot flag. global.js's
      // updatePickerInnerHtml replaces the whole variant picker's innerHTML on
      // a variant change, which throws these buttons away — and a dataset flag
      // would either go with them (leaving nothing to re-run) or, on a fieldset
      // that survived, wrongly report the row as already wired. Asking the DOM
      // is the only answer that stays true across a rewrite.
      if (fieldset.querySelector('.bj-swatch-nav')) return;
      var row = fieldset.querySelector('.product-form__controls-group');
      if (!row) return;

      // Page by whole swatches so a colour is never left half-cut at the edge,
      // keeping one in view as an anchor between pages.
      function step(direction) {
        var swatch = row.querySelector('label.variant-swatch');
        var unit = (swatch && swatch.offsetWidth) || 50;
        var perPage = Math.max(1, Math.floor((row.clientWidth - unit) / unit));
        var distance = direction * perPage * unit;
        if (typeof row.scrollBy === 'function') {
          try {
            row.scrollBy({ left: distance, behavior: 'smooth' });
            return;
          } catch (e) {
            /* older Safari: options object unsupported, fall through */
          }
        }
        row.scrollLeft += distance;
      }

      var prev = makeSwatchNav(fieldset, 'prev', 'Show previous colours');
      var next = makeSwatchNav(fieldset, 'next', 'Show more colours');
      prev.addEventListener('click', function () {
        step(-1);
      });
      next.addEventListener('click', function () {
        step(1);
      });

      row.addEventListener('scroll', function () {
        syncSwatchRow(fieldset, row);
      });

      // The first measure can land before the row has its final width — the
      // swatches are background-images on empty labels, and the info column
      // is laid out against media that may still be loading. A stale "does
      // not overflow" reading leaves is-scrollable false, which hides both
      // arrows and makes the row look like it simply has nothing more to
      // show. Re-measure whenever the box actually changes.
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(function () {
          syncSwatchRow(fieldset, row);
        }).observe(row);
      }
      syncSwatchRow(fieldset, row);
    });
  }

  /* ------------------------------------------------ 4. description clamp */

  function measureClamp(root) {
    each((root || document).querySelectorAll('[data-bj-clamp]'), function (wrapper) {
      var toggle = wrapper.querySelector('[data-bj-clamp-toggle]');
      if (!toggle) return;
      if (wrapper.classList.contains('is-expanded')) return;
      // The clamp is on the wrapper, so a description that fits reports the
      // same scroll and client height and needs no control at all. The flag is
      // negative on purpose: assets/global.js replaces this whole subtree on
      // every variant change, and an un-measured fresh copy should default to
      // showing the control rather than silently swallowing overflowing copy.
      var clipped = wrapper.scrollHeight - wrapper.clientHeight > 1;
      wrapper.classList.toggle('is-unclipped', !clipped);
    });
  }

  function watchDescription(root) {
    if (typeof MutationObserver !== 'function') return;
    each((root || document).querySelectorAll('.product__description'), function (el) {
      if (el.dataset.bjDescReady === 'true') return;
      el.dataset.bjDescReady = 'true';
      // global.js rewrites this element's innerHTML on every variant change, so
      // the clamp has to be re-measured against the new copy. The element
      // itself survives, which is what keeps this observer attached.
      new MutationObserver(function () {
        measureClamp(el);
      }).observe(el, { childList: true });
    });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-bj-clamp-toggle]');
    if (!toggle) return;
    var wrapper = toggle.closest('[data-bj-clamp]');
    if (!wrapper) return;

    var expanded = wrapper.classList.toggle('is-expanded');
    toggle.textContent = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  });

  /* -------------------------------------------------------------- wiring */

  // Each step is isolated: they are independent behaviours on one page, and
  // before this a throw in any of them killed every step after it. That failure
  // is silent and looks like a CSS bug — an exception in initSizeSelects would
  // take out the colour-row arrows, whose absence reads as "the affordance is
  // not styled" rather than "the script died three functions ago".
  function step(name, fn, root) {
    try {
      fn(root);
    } catch (err) {
      console.error('[bj-size-select] ' + name + ' failed', err);
    }
  }

  function init(root) {
    step('initSizeSelects', initSizeSelects, root);
    step('watchBuyState', watchBuyState, root);
    step('watchSwatchRow', watchSwatchRow, root);
    step('measureSwatchRow', measureSwatchRow, root);
    step('watchDescription', watchDescription, root);
    step('measureClamp', measureClamp, root);
    step('watchPickerRewrites', watchPickerRewrites, root);
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      measureSwatchRow(document);
      measureClamp(document);
    }, 150);
  });

  // The variant picker is replaced wholesale on a variant change
  // (updatePickerInnerHtml in assets/global.js), taking the colour row and its
  // arrows with it. Nothing re-runs init at that point, so the row came back
  // unwired. Watching the picker for child changes re-attaches them; the guard
  // above makes the re-run a no-op when they are already there, so our own
  // appendChild cannot loop.
  function watchPickerRewrites(root) {
    if (typeof MutationObserver !== 'function') return;
    each((root || document).querySelectorAll('variant-radios, variant-selects'), function (picker) {
      if (picker.dataset.bjPickerWatched === 'true') return;
      picker.dataset.bjPickerWatched = 'true';
      var pending;
      new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(function () {
          step('watchSwatchRow', watchSwatchRow, picker);
          step('measureSwatchRow', measureSwatchRow, picker);
        }, 50);
      }).observe(picker, { childList: true, subtree: true });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  // Fonts and media settle after DOMContentLoaded and can change the row's
  // width in either direction, so the overflow flags are re-read once the
  // page has finished loading.
  window.addEventListener('load', function () {
    // watchSwatchRow as well as the measures: by this point the picker may
    // already have been rewritten, in which case re-measuring alone would set
    // is-scrollable on a fieldset that has no arrows to reveal.
    step('watchSwatchRow', watchSwatchRow, document);
    step('measureSwatchRow', measureSwatchRow, document);
    step('measureClamp', measureClamp, document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  if (document.readyState !== 'loading') init(document);
})();
